<template>
  <header :class="['header', {
    'header--scrolled': isScrolled,
    'is-active': isActive,
    'search-active': isSearchActive
  }]">
    <div class="container">
      <div class="header__left" :class="{
         'header__left_ru': currentLanguage && currentLanguage.startsWith('ru'),
         'header__left_fr': currentLanguage && currentLanguage.startsWith('fr'),
          'header__left_es': currentLanguage && currentLanguage.startsWith('es')
      }">
        <div @click="handleLogoClick" class="header__logo">
          <img :src="isActive? logoActive : logoNormal" alt="亚科Logo">
        </div>

        <nav class="header__nav" :class="{
          'header__nav_en': currentLanguage && currentLanguage.startsWith('en'),
          'header__nav_ja': currentLanguage && currentLanguage.startsWith('ja'),
          'header__nav_ko': currentLanguage && currentLanguage.startsWith('ko'),
          'header__nav_ru': currentLanguage && currentLanguage.startsWith('ru'),
          'header__nav_fr': currentLanguage && currentLanguage.startsWith('fr'),
          'header__nav_es': currentLanguage && currentLanguage.startsWith('es')
        }" v-show="!isSearchActive">
          <div
              v-for="(item, index) in menuItems"
              :key="item.path"
              class="nav-item-wrapper"
              @mouseenter="handleMenuHover(item.title, index)"
              @mouseleave="handleMenuLeave"
          >
            <div
                @click="handleMenuClick(item)"
                class="header__nav-item"
                :class="[
                  currentLanguage && currentLanguage.startsWith('en') ? 'header__nav-item_en' : '',
                  currentLanguage && currentLanguage.startsWith('ja') ? 'header__nav-item_ja' : '',
                  currentLanguage && currentLanguage.startsWith('ko') ? 'header__nav-item_ko' : '',
                  currentLanguage && currentLanguage.startsWith('ru') ? 'header__nav-item_ru' : '',
                  currentLanguage && currentLanguage.startsWith('fr') ? 'header__nav-item_fr' : '',
                   currentLanguage && currentLanguage.startsWith('es') ? 'header__nav-item_es' : '',
                  activeMenuItem === item.title ? 'is-hover' : '',
                  isMenuActive(item) ? 'is-active' : ''
                ]"
            >
              {{ item.title }}
            </div>
            
            <!-- 二级菜单 -->
            <div class="submenu" v-if="item.children && item.children.length" :class="{'submenu-active': activeMenuItem === item.title}">
              <div class="submenu-container">
                <div class="submenu-content">
                  <div class="submenu-list">
                    <div
                      v-for="(subItem, subIndex) in item.children"
                      :key="subIndex"
                      @click="handleMenuClick(subItem)"
                      :class="['submenu-link', { 'submenu-link-active': isSubMenuActive(subItem) }]"
                    >
                      {{ subItem.title }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="nav-slider" :style="sliderStyle"></div>
        </nav>

        <div class="header-search" :class="{ 'is-active': isSearchActive }">
          <div class="search-wrapper">
            <i class="el-icon-search search-icon"></i>
            <input
                type="text"
                v-model="searchKeyword"
                :placeholder="$t('header.searchPlaceholder')"
                @keyup.enter="handleSearch"
                ref="searchInput"
            >
            <div class="close-btn" @click="closeSearch">
              <i class="el-icon-close"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="header__right">
        <div class="header-tools" v-show="!isSearchActive">
          <div class="search-btn" @click="showSearch">
            <i class="el-icon-search"></i>
          </div>
          <div class="world-icon">
            <img :src="isActive? dqActive : dqNormal" alt="world">
          </div>
          <LangSwitch :is-scrolled="isScrolled" />
          <div class="contact-btn" :class="{'contact-btn-scrolled': isScrolled}" v-if="showContactBtn">
            <img 
              :src="linkActiveIcon" 
              alt="contact" 
              @click="handleContactClick"
              class="contact-icon"
            />
            <div class="contact-tooltip">{{$t('header.contactUs')}}</div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import {getRoutes} from "@/api/menu";
import { pushWithLanguage } from '@/utils/routeUtils'

export default {
  name: 'TheHeader',
  components: {
    LangSwitch: () => import('@/components/LangSwitch')
  },
  data() {
    return {
      isScrolled: true,
      menuItems: [],
      isActive: true,
      logoNormal: require('@/assets/logo.png'),
      logoActive: require('@/assets/logo-active.png'),
      dqNormal: require('@/assets/images/dq.png'),
      dqActive: require('@/assets/images/dq-active.png'),
      linkActiveIcon: require('@/assets/images/link-active.png'),
      isSearchActive: false,
      searchKeyword: '',
      showContactBtn: true,
      activeMenuItem: null,
      sliderLeft: 0,
      sliderWidth: 0,
      activeMenuIndex: -1,
      activeMenuPath: '',
      activeSubMenuPath: ''
    }
  },
  computed: {
    ...mapState({
      currentLanguage: state => state.app.language
    }),
    sliderStyle() {
      return {
        left: `${this.sliderLeft}px`,
        width: `${this.sliderWidth}px`,
        opacity: this.sliderWidth > 0 ? 1 : 0
      }
    },
    worldLabel() {
      try {
        const nav = typeof navigator !== 'undefined' ? navigator : null
        const raw = nav && Array.isArray(nav.languages) && nav.languages.length ? nav.languages[0] : (nav && (nav.language || nav.userLanguage)) || ''
        const lang = String(raw || '').replace('_', '-').toLowerCase()
        const parts = lang.split('-')
        if (parts.length > 1 && parts[1]) return parts[1].toUpperCase()
        const map = { zh: 'CN', en: 'US', ja: 'JP', ko: 'KR', ru: 'RU', fr: 'FR', es: 'ES', ar: 'SA' }
        return map[parts[0]] || 'US'
      } catch (e) {
        return 'US'
      }
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.$nextTick(() => {
      this.updateSliderPosition()
    })
    // 检查并初始化菜单缓存
    this.initMenuCache();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },

  // 添加路由监听器
  watch: {
    '$route'(to, from) {
      // 每次路由变化时重新调用 initMenuCache
      this.initMenuCache();
      // 更新滑块位置
      this.$nextTick(() => {
        this.updateSliderPosition();
      });
    },
    currentLanguage(newValue) {
      this.initMenuCache()
      // 语言切换时更新滑块位置
      this.$nextTick(() => {
        this.updateSliderPosition();
      });
    }
  },

  created() {
  },
  methods: {
    ...mapActions(['setLanguage', 'setMetaInfo']),

    // 处理 Logo 点击事件
    handleLogoClick() {
      const currentPath = this.$route.path
      const targetPath = this.generateLanguagePath('/')
      
      if (currentPath === targetPath) {
        // 已经在首页，不做跳转
        return
      }
      
      // 使用 pushWithLanguage 跳转到首页
      pushWithLanguage('/', this.currentLanguage).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('首页跳转失败:', err)
        }
      })
    },

    async initMenuCache() {
      try {
        const routers = await getRoutes();
        if (routers.success) {
          // 动态构建菜单项
          const menuData = routers.data;
          this.menuItems = menuData.map(item => {
            const menuItem = {
              ...item,
              ...{path: '/' + (item.url || item.path || '')}
            };
            
            // 如果有子菜单，构建二级菜单
            if (item.children && item.children.length > 0) {
              menuItem.children = item.children.map(child => ({
                ...child ,
                ...{path: '/' + (item.url || '') + '/' + (child.url || '')} // 拼接父级url和子级url
              }));
            }
            return menuItem;
          });
          // 将路由数据存储到本地缓存
          localStorage.setItem('menuItems', JSON.stringify(this.menuItems));
          this.$nextTick(() => {
            this.updateSliderPosition();
          });
        }
      } catch (e) {
        console.error('获取菜单数据失败', e);
        // 兜底：如果接口失败，尝试读取本地缓存
        let menuItems = localStorage.getItem('menuItems');
        if (menuItems) {
          try {
            this.menuItems = JSON.parse(menuItems);
            this.$nextTick(() => {
              this.updateSliderPosition();
            });
          } catch (parseError) {
            console.error('解析菜单缓存失败', parseError);
          }
        }
      }
    },

    //菜单跳转 - 修改为使用 pushWithLanguage
    handleMenuClick(item) {
      console.log('=====', item)
      const currentPath = this.$route.path
      const targetPath = this.generateLanguagePath(item.path)

      // 先更新页面 meta 信息到缓存（store）
      try {
        const metaInfo = {
          title: item.seoTitle || item.title || '',
          keywords: item.keywords || '',
          description: item.description || ''
        }
        this.$store.commit("CHANGE_META_INFO", metaInfo)
      } catch (e) {
        console.error('更新页面 Meta 信息失败:', e)
      }
      
      if (currentPath === targetPath) {
        // 已经在当前页面，不做跳转
        return
      }
      
      // 使用 pushWithLanguage 进行跳转
      pushWithLanguage(item.path, this.currentLanguage).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('路由跳转失败:', err)
        }
      })
    },

    // 生成带语言前缀的路径
    generateLanguagePath(path) {
      const currentLang = this.currentLanguage || 'en'
      const cleanPath = path.startsWith('/') ? path : '/' + path
      
      // 如果路径已经包含语言前缀，直接返回
      if (cleanPath.startsWith(`/${currentLang}/`) || 
          (currentLang === 'en' && !cleanPath.startsWith('/zh/') && !cleanPath.startsWith('/ja/') && !cleanPath.startsWith('/ko/') && !cleanPath.startsWith('/ru/') && !cleanPath.startsWith('/es/') && !cleanPath.startsWith('/fr/') && !cleanPath.startsWith('/ar/'))) {
        return cleanPath
      }
      
      // 如果是英文，不添加语言前缀
      if (currentLang === 'en') {
        return cleanPath
      }
      
      // 添加语言前缀
      return `/${currentLang}${cleanPath}`
    },

    handleScroll() {
      // this.isScrolled = window.scrollY > 50
      // this.isActive = window.scrollY > 0
      
      // 当页面滚动时，自动关闭搜索框
      if (this.isSearchActive) {
        this.closeSearch()
      }
    },
    showSearch() {
      this.isSearchActive = true
      this.$nextTick(() => {
        this.$refs.searchInput.focus()
      })
    },
    closeSearch() {
      this.isSearchActive = false
      this.searchKeyword = ''
    },
    handleSearch() {
      if (this.searchKeyword.trim()) {
        pushWithLanguage('/SearchPage', this.currentLanguage).then(() => {
          // 跳转成功后更新查询参数
          this.$router.replace({
            path: this.$route.path,
            query: { keyword: this.searchKeyword }
          })
          this.closeSearch()
        }).catch(err => {
          console.error('搜索页面跳转失败:', err)
        })
      }
    },
    handleMenuHover(title, index) {
      this.activeMenuItem = title
      this.updateSliderForIndex(index)
    },
    handleMenuLeave() {
      this.activeMenuItem = null
      this.updateSliderPosition()
    },
    handleContactClick() {
      pushWithLanguage('/contact-us', this.currentLanguage).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('联系我们页面跳转失败:', err)
        }
      })
    },
    updateSliderPosition() {
      const currentPath = this.normalizePath(this.$route.path || '/')
      let matchedMenuPath = ''
      let matchedSubPath = ''
      const activeIndex = this.menuItems.findIndex(item => {
        const normalizedItemPath = this.normalizePath(item.path || '/')
        const isMenuMatched = this.isPathMatched(currentPath, normalizedItemPath)
        if (isMenuMatched) {
          matchedMenuPath = normalizedItemPath
          if (item.children && item.children.length) {
            const matchedSub = item.children.find(subItem => {
              const normalizedSubPath = this.normalizePath(subItem.path || '')
              const isSubMatched = this.isPathMatched(currentPath, normalizedSubPath)
              if (isSubMatched) {
                matchedSubPath = normalizedSubPath
              }
              return isSubMatched
            })
            if (!matchedSub) {
              matchedSubPath = ''
            }
          } else {
            matchedSubPath = ''
          }
          return true
        }
        return false
      })

      this.activeMenuPath = matchedMenuPath
      this.activeSubMenuPath = matchedSubPath
      
      if (activeIndex > -1) {
        this.updateSliderForIndex(activeIndex)
        this.activeMenuIndex = activeIndex
      } else {
        // 如果没有活动菜单项，隐藏滑块
        this.sliderWidth = 0
        this.activeMenuIndex = -1
      }
    },
    updateSliderForIndex(index) {
      if (index < 0) return
      
      this.$nextTick(() => {
        const navItems = this.$el.querySelectorAll('.nav-item-wrapper')
        if (navItems && navItems[index]) {
          const item = navItems[index]
          const navEl = this.$el.querySelector('.header__nav')
          
          if (item && navEl) {
            const itemRect = item.getBoundingClientRect()
            const navRect = navEl.getBoundingClientRect()
            
            this.sliderLeft = itemRect.left - navRect.left + (30) / 2
            this.sliderWidth = itemRect.width - 30
          }
        }
      })
    },
    normalizePath(path = '') {
      const value = path.startsWith('/') ? path : `/${path}`
      const segments = value.split('/').filter(Boolean)
      if (!segments.length) return '/'
      const supportedLangs = ['zh', 'en', 'ja', 'ko', 'ru', 'fr', 'es', 'ar']
      if (supportedLangs.includes(segments[0])) {
        segments.shift()
      }
      const normalized = `/${segments.join('/')}`.replace(/\/+/g, '/')
      return normalized === '/' ? normalized : normalized.replace(/\/$/, '')
    },
    isPathMatched(current, target) {
      if (!target || target === '/') {
        return current === '/'
      }
      return current === target || current.startsWith(`${target}/`)
    },
    isMenuActive(item) {
      const normalizedItemPath = this.normalizePath(item.path || '/')
      return normalizedItemPath === this.activeMenuPath
    },
    isSubMenuActive(subItem) {
      const normalizedSubPath = this.normalizePath(subItem.path || '')
      return normalizedSubPath && normalizedSubPath === this.activeSubMenuPath
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/styles/variables.less';
@import '../assets/styles/mixins.less';

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  background: transparent;
  padding: 70px 0 20px;
  border-bottom: 3px solid rgba(255, 255, 255, 0.2);

  .header__logo {
    display: flex;
    align-items: center;
    cursor: pointer; // 添加鼠标指针样式
    
    img {
      height: 56px;
      margin-top: -20px;
    }
  }

  &--scrolled {
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border-bottom: none;

    .header-tools {
      .search-btn {
        background: rgba(0, 0, 0, 0.05);

        &:hover {
          background: rgba(0, 0, 0, 0.08);
        }

        i {
          color: #333;
        }
      }

      .lang-switch {
        :deep(.lang-label) {
          color: var(--primary-color);
        }
      }

      .contact-btn a {
        background: #19529F;
        color: #fff;
        font-size: @font-size-note !important;
      }
    }

    .header__nav-item {
      color: #333;
      cursor: pointer;
    }

    .submenu {
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }
  }

  .container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1520px;
    margin: 0 auto;
    padding: 0;
    height: 100%;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 60px;
    height: 100%;
  }
  &__left_ru,&__left_fr,&__left_es{
    gap: 4px !important;
  }

  &__right {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: 100%;
  }

  &__logo {
    display: block;

    img {
      height: 36px;
      transition: all 0.3s ease;
    }
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: 25px;
    height: 100%;
    position: relative;
  }
  &__nav_en {
    gap: 10px !important;
  }
  &__nav_ja {
    gap: 10px !important;
  }
  &__nav_ko {
    gap: 10px !important;
  }
  &__nav_ru {
    gap: 10px !important;
  }
  &__nav_fr {
    gap: 10px !important;
  }
  &__nav_es {
    gap: 10px !important;
  }

  .nav-slider {
    position: absolute;
    bottom: -23px;
    height: 3px;
    background-color: #fff;
    transition: all 0.3s ease;
    border-radius: 3px;
  }

  .nav-item-wrapper {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 10px;
  }
  .header__nav_ja .nav-item-wrapper,
  .header__nav_ko .nav-item-wrapper,
  .header__nav_fr .nav-item-wrapper{
    padding: 0 6px;
  }

  .header__nav_ru .nav-item-wrapper{
    padding: 0 3px;
  }

  &__nav-item {
    color: #fff;
    text-decoration: none;
    font-size: @font-size-note;

    white-space: nowrap;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    padding: 5px 0;
    position: relative;
    height: 100%;
    cursor: pointer;

    &.is-hover:not(.router-link-active)::after {
      width: 20px;
      opacity: 0.7;
    }
    
    &.is-active {
      color: #fff;
      font-weight: 600;
    }
    
    .submenu-icon {
      margin-left: 5px;
      font-size: 12px;
      transition: transform 0.3s;
    }
    
    &.is-hover .submenu-icon {
      transform: rotate(180deg);
    }
  }

  &__nav-item_en {
    font-size: @font-size-note;
  }
  &__nav-item_ja {
    font-size: 14px;
    padding: 0;
  }
  &__nav-item_ko {
    font-size:  @font-size-note;
    padding: 0;
  }
  &__nav-item_ru {
    font-size: 14px;
  }

  &__nav-item_es {
    font-size: 16px;
  }

  &__nav-item_fr {
    font-size: 14px;
  }

  /* 二级菜单样式 - 按照图片样式调整 */
  .submenu {
    position: absolute;
    top: calc( 100% + 15px );
    left: 0;
    transform: translateX(-20%) translateY(10px);
    width: 280px;
    background: #fff;
    border-radius: 0;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s;
    z-index: 100;
    padding: 20px 0 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    text-align: left;
    
    &-active {
      opacity: 1;
      visibility: visible;
      transform: translateX(10px) translateY(8px);
    }
    
    &-container {
      width: 100%;
      padding: 0;
    }
    
    &-content {
      display: flex;
      flex-direction: column;
    }
    
    &-list {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    
    &-link {
      color: #666;
      text-decoration: none;
      font-size: @font-size-note;
      font-weight: 400;
      padding: 16px 30px;
      text-align: left;
      transition: all 0.3s;
      cursor: pointer;
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        color: #19529F;
        background-color: rgba(25, 82, 159, 0.05);
      }
      
      &.submenu-link-active {
        color: #19529F;
        font-weight: 600;
        background-color: rgba(25, 82, 159, 0.08);
      }
    }
  }

  &.search-active {
    .header__nav,
    .header-tools {
      display: none;
    }

    .header-search {
      opacity: 1;
      visibility: visible;
      transform: translateX(-50%) translateY(0);
    }

    background: #fff !important;

    .header__logo {
      position: relative;
      z-index: 2;
    }
  }

  &.is-active {
    .header__logo img {
      transform: scale(0.95);
    }
  }
}

.header--scrolled {
  .header__nav-item.is-active {
    color: var(--primary-color);
  }
}

.header-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;

  .lang-switch {
    :deep(.lang-label) {
      color: #fff;
    }
  }

  .search-btn {
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s;
    background: var(--primary-color);
    border: 2px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(25, 82, 159, 0.3);

    &:hover {
      background: rgba(25, 82, 159, 1);
      border-color: rgba(255, 255, 255, 0.5);
      box-shadow: 0 6px 20px rgba(25, 82, 159, 0.4);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(25, 82, 159, 0.3);
    }

    i {
      font-size: @font-size-note;
      color: #fff;
      font-weight: 600;
    }
  }

  .world-icon {
    height: 40px;
    display: flex;
    align-items: center;
    position: relative;
    margin-left: 20px;
      img{
        width: 20px;
        height: 20px;
      }
    .world-label {
      color: #333;
      font-size: @font-size-note;
    }
  }

  .contact-btn {
    display: flex;
    align-items: center;
    height: 100%;
    position: relative;

    .contact-icon {
      width: 20px;
      height: 20px;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        transform: scale(1.1);
      }
    }

    .contact-tooltip {
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      margin-bottom: 8px;
      padding: 6px 12px;
      background: rgba(0, 0, 0, 0.8);
      color: #fff;
      font-size: @font-size-note;
      border-radius: 4px;
      white-space: nowrap;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s;
      z-index: 1000;
      
      &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 4px solid transparent;
        border-top-color: rgba(0, 0, 0, 0.8);
      }
    }

    &:hover .contact-tooltip {
      opacity: 1;
      visibility: visible;
    }
  }
}

.header-search {
  position: absolute;
  left: 50%;
  transform: translateX(-50%) translateY(-20px);
  width: 60%;
  max-width: 1000px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 40px;
  z-index: 1;

  &.is-active {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  .search-wrapper {
    height: 100%;
    display: flex;
    align-items: center;
    background: transparent;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    position: relative;
    padding: 0 40px;
  }

  .search-icon {
    position: absolute;
    left: 0;
    font-size: @font-size-note;
    color: #999;
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    font-size: @font-size-note;
    color: #333;
    outline: none;
    padding: 0;

    &::placeholder {
      color: #999;
    }
  }

  .close-btn {
    position: absolute;
    right: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;
    border-radius: 50%;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    i {
      font-size: @font-size-note;
      color: #999;
    }
  }
}

</style>